import React, { useEffect } from 'react';
import { Clock, LogOut } from 'lucide-react';
import type { WarningType } from '../hooks/useSessionTimeout';

interface SessionTimeoutWarningProps {
  timeRemaining: number;
  warningType: WarningType;
  onContinue: () => void;
  onSignOut: () => void;
  onExpired: () => void;
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  timeRemaining,
  warningType,
  onContinue,
  onSignOut,
  onExpired,
}) => {
  // Auto-trigger sign out when countdown reaches zero
  useEffect(() => {
    if (timeRemaining <= 0) {
      onExpired();
    }
  }, [timeRemaining, onExpired]);

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const countdown = minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, '0')}`
    : `${seconds}s`;

  const isAbsolute = warningType === 'absolute';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
            <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-medical-900 dark:text-gray-100">
            Session Expiring
          </h3>
        </div>

        {/* Body */}
        <div className="px-6 pb-4 text-center">
          <div className="text-3xl font-mono font-bold text-amber-600 dark:text-amber-400 mb-3">
            {countdown}
          </div>
          <p className="text-sm text-medical-600 dark:text-gray-400">
            {isAbsolute
              ? 'Your session will expire shortly. For security, sessions are limited to 12 hours. Please save your work and sign in again.'
              : 'Your session will expire due to inactivity. Would you like to continue?'}
          </p>
          <p className="text-xs text-medical-500 dark:text-gray-500 mt-2">
            To protect sensitive clinical data, ClinLexis automatically signs you out after a period of inactivity. This is a requirement of NHS data security standards.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3 justify-center">
          <button
            onClick={onSignOut}
            className="btn-secondary flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
          {!isAbsolute && (
            <button
              onClick={onContinue}
              className="btn-primary"
            >
              Continue Session
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
